import React, { useState, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { observer } from 'mobx-react-lite';
import globalSettingsStores from '../../../../stores/settings-stores';
import { PollWithQuestionsResponse } from '../../../../api/types/settings/settings-all';
import { createCourseFeedbackOperation } from '../operations/create-course-feedback-operation';
import globalCourseStore from '../../../../stores/global-course-store';
import { LecturerType } from '../../../../api/types/programs/course/lecturers/course-lecturer';
import { CoursePollWithVotesResponse } from '../../../../api/types/students/course-poll-feedbacks/course-poll-with-questions';

export interface AddFeedbackDialogProps {
  programId: number;
  courseId: number;
  closeDialog: () => void;
}

export const AddFeedbackDialog: React.FC<AddFeedbackDialogProps> = observer(
  ({ programId, courseId, closeDialog }) => {
    const [selectedPoll, setSelectedPoll] =
      useState<PollWithQuestionsResponse | null>(null);
    const [selectedProfessor, setSelectedProfessor] =
      useState<LecturerType | null>(null);
    const [isAnonymous, setIsAnonymous] = useState(true);
    const [blockGradeView, setBlockGradeView] = useState(false);
    const [loading, setLoading] = useState(false);

    const abstractPolls = globalSettingsStores.abstractPolls || [];

    // Get lecturers from course details
    const lecturers: LecturerType[] = useMemo(() => {
      const currentCourse = globalCourseStore.getCurrentCourse();
      return currentCourse?.details?.lecturer || [];
    }, [globalCourseStore.getCurrentCourse()?.details?.lecturer]);

    // Get existing polls from course details and filter out already used abstract polls
    const availableAbstractPolls: PollWithQuestionsResponse[] = useMemo(() => {
      const currentCourse = globalCourseStore.getCurrentCourse();
      const existingPolls: CoursePollWithVotesResponse[] =
        currentCourse?.details?.polls || [];

      // Get the abstract poll IDs that are already used in this course
      const usedAbstractPollIds = new Set(existingPolls.map((poll) => poll.id));

      // Filter out abstract polls that are already used
      return abstractPolls.filter(
        (abstractPoll) => !usedAbstractPollIds.has(abstractPoll.id),
      );
    }, [abstractPolls, globalCourseStore.getCurrentCourse()?.details?.polls]);

    const handleSubmit = async () => {
      if (!selectedPoll || !selectedProfessor) return;

      await createCourseFeedbackOperation(
        programId,
        courseId,
        selectedProfessor.id,
        selectedPoll.id,
        true, // isActive - default to true when creating
        isAnonymous,
        blockGradeView,
        setLoading,
        closeDialog,
      );
    };

    return (
      <div style={{ direction: 'rtl', padding: '8px' }}>
        {/* Professor Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 500,
              color: '#374151',
            }}
          >
            בחר מרצה <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <Dropdown
            value={selectedProfessor}
            onChange={(e) => setSelectedProfessor(e.value)}
            options={lecturers}
            itemTemplate={(lecturer: LecturerType) =>
              `${lecturer.firstName} ${lecturer.lastName}`
            }
            valueTemplate={(lecturer: LecturerType | null) =>
              lecturer
                ? `${lecturer.firstName} ${lecturer.lastName}`
                : 'בחר מרצה מהרשימה'
            }
            placeholder="בחר מרצה מהרשימה"
            className="w-full"
            style={{ width: '100%' }}
            emptyMessage="לא נמצאו מרצים"
          />
          {lecturers.length === 0 && (
            <div
              style={{
                marginTop: '8px',
                padding: '8px 12px',
                backgroundColor: '#fef2f2',
                borderRadius: '6px',
                border: '1px solid #fecaca',
                fontSize: '13px',
                color: '#dc2626',
              }}
            >
              <i
                className="pi pi-exclamation-triangle"
                style={{ marginLeft: '6px' }}
              />
              אין מרצים בקורס זה
            </div>
          )}
        </div>

        {/* Poll Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 500,
              color: '#374151',
            }}
          >
            בחר משוב <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <Dropdown
            value={selectedPoll}
            onChange={(e) => setSelectedPoll(e.value)}
            options={availableAbstractPolls}
            optionLabel="pollName"
            placeholder="בחר משוב מהרשימה"
            className="w-full"
            style={{ width: '100%' }}
            emptyMessage="לא נמצאו משובים זמינים"
          />
          {selectedPoll && (
            <div
              style={{
                marginTop: '8px',
                padding: '8px 12px',
                backgroundColor: '#f0f9ff',
                borderRadius: '6px',
                border: '1px solid #bae6fd',
                fontSize: '13px',
                color: '#0369a1',
              }}
            >
              <i className="pi pi-info-circle" style={{ marginLeft: '6px' }} />
              המשוב מכיל {selectedPoll.questions?.length || 0} שאלות
            </div>
          )}
        </div>

        {/* Anonymous Option */}
        <div
          style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
            }}
          >
            <Checkbox
              inputId="isAnonymous"
              checked={isAnonymous}
              tooltip={'כלל המשובים אנונימים'}
              /*onChange={(e) => setIsAnonymous(e.checked ?? true)}*/
              style={{ marginTop: '2px' }}
            />
            <div>
              <label
                htmlFor="isAnonymous"
                style={{
                  fontWeight: 500,
                  color: '#374151',
                  cursor: 'pointer',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                משוב אנונימי
              </label>
              <span style={{ color: '#6b7280', fontSize: '13px' }}>
                כאשר מסומן, זהות הסטודנט לא תוצג בתשובות המשוב. המרצה יראה רק את
                התשובות ללא שמות.
              </span>
            </div>
          </div>
        </div>

        {/* Block Grade View Option */}
        <div
          style={{
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
            }}
          >
            <Checkbox
              inputId="blockGradeView"
              checked={blockGradeView}
              onChange={(e) => setBlockGradeView(e.checked ?? false)}
              style={{ marginTop: '2px' }}
            />
            <div>
              <label
                htmlFor="blockGradeView"
                style={{
                  fontWeight: 500,
                  color: '#374151',
                  cursor: 'pointer',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                חסום צפייה בציונים עד למילוי המשוב
              </label>
              <span style={{ color: '#6b7280', fontSize: '13px' }}>
                כאשר מסומן, הסטודנט לא יוכל לצפות בציוני הקורס עד שימלא את
                המשוב. אפשרות זו מעודדת סטודנטים למלא את המשוב.
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: '8px',
          }}
        >
          <Button
            label="הוסף משוב"
            icon="pi pi-plus"
            onClick={handleSubmit}
            loading={loading}
            tooltip={
              !selectedPoll || !selectedProfessor || lecturers.length === 0
                ? 'יש לבחור מרצה'
                : 'לחץ להוספת המשוב לקורס'
            }
            tooltipOptions={{ showOnDisabled: true }}
            disabled={
              !selectedPoll || !selectedProfessor || lecturers.length === 0
            }
          />
          <Button
            label="ביטול"
            icon="pi pi-times"
            className="p-button-secondary p-button-outlined"
            onClick={closeDialog}
            disabled={loading}
          />
        </div>
      </div>
    );
  },
);
